import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import CustomTooltip from '../../../Tooltip';
import Loading from '../../../Loading';
import { Link } from '../../../RouterCompatibility';
import { statuses } from '../../../../config';
import {
    getAvailableDevices
} from '../../../modules';
import {
    renderCplAmount,
    renderCps,
    renderCplAmountMin
} from '../../../modules/index';

const OfferPageGoals = ({ 
    offer, 
    goalEntries, 
    goalActions, 
    regions, 
    loading, 
    t 
}) => {
    const [state, setState] = useState({
        offer: offer || {},
        goalEntries: goalEntries || [],
        goalActions: goalActions || [],
        regions: regions || {},
        loading: loading || false
    });

    const referralBadges = (t && t.referral) || {};

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            offer: offer || {},
            goalEntries: goalEntries || [],
            goalActions: goalActions || [],
            regions: regions || {},
            loading: loading || false
        }));
    }, [offer, goalEntries, goalActions, regions, loading]);

    const showConstructor = useCallback((goalEntryId, access) => {
        // This would need to be passed as a prop or context
        // For now, we'll assume it's available in context
        if (typeof window !== 'undefined' && window.showConstructor) {
            window.showConstructor({
                'linkId': state.offer.default_link?.id,
                'goalEntryId': goalEntryId,
                'access': access
            });
        }
    }, [state.offer.default_link?.id]);

    const getAvailableRegions = useCallback((goalEntry) => {
        let regions = goalEntry.goal.traffic_restrictions.regions.map((regionId) => {
            return state.regions[regionId];
        });
        return regions.join(', ');
    }, [state.regions]);

    const getGoalActions = useCallback((goalId) => {
        return state.goalActions.filter((goalAction) => {
            return goalAction.goal.id === goalId;
        });
    }, [state.goalActions]);

    const renderConversionCap = useCallback(({ conversion_cap }) => {
        if (!conversion_cap) {
            return null;
        }

        const { period, cap_limit } = conversion_cap;
        const { periods } = t;
        const { cap, conversions } = t?.offersPage?.offer || {};
        return (
            <span className="goals-cap">{periods[period]} {cap} - {cap_limit} {conversions}</span>
        );
    }, [t]);

    const renderBadge = useCallback((goalEntry) => {
        if (goalEntry.offer.referral_offer && goalEntry.offer.advertiser.referral_advertiser) {
            return (
                <span className="badge badge-danger ml10" title={referralBadges.offer}>RO</span>
            );
        }
        return null;
    }, [referralBadges]);

    const renderGoalActions = useCallback((goalEntry, index) => {
        const staticNames = t?.dataTable?.headers || {};

        return (
            <div className="item-actions" key={index}>
                <div className="item-actions__wrapper">
                    <div className="item-actions__wrapper-header">
                        <div className="header-item">{staticNames.action}</div>
                        <div className="header-item">{staticNames.type}</div>
                        <div className="header-item">{staticNames.subValue}</div>
                        <div className="header-item">{staticNames.cpaFixed}</div>
                        <div className="header-item">{staticNames.cpaShared}</div>
                        <div className="header-item">{staticNames.cplAmountMin}</div>
                    </div>
                    <div className="item-actions__wrapper-body">
                        {getGoalActions(goalEntry.goal.id).map((goalAction, i) => {
                            return (
                                <div className="body-wrap" key={i}>
                                    <div className="body-item">{goalAction.action.title}</div>
                                    <div className="body-item">{goalAction.action.type}</div>
                                    <div className="body-item">({goalAction.sub_goal_action_dictionary.sub_value}) {goalAction.sub_goal_action_dictionary.title}</div>
                                    <div className="body-item">{goalAction.commission_contract && renderCplAmount(goalAction.commission_contract, `commissionContract-${index}-${i}`)}</div>
                                    <div className="body-item">{goalAction.commission_contract && renderCps(goalAction.commission_contract, `interestContract-${index}-${i}`)}</div>
                                    <div className="body-item">{goalAction.commission_contract && renderCplAmountMin(goalAction.commission_contract, `interestContract-${index}-${i}`)}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }, [getGoalActions, t, renderCplAmount, renderCps, renderCplAmountMin]);

    const renderGoalEntries = useCallback((goalEntry, key) => {
        let accessIconClass = goalEntry.access_granted ? 'unlock' : 'lock';
        let regions = getAvailableRegions(goalEntry);
        let devices = getAvailableDevices(goalEntry.goal.traffic_restrictions.devices);
        let staticNames = t?.offersPage?.offer || {};

        return (
            <div className="offer-item__goals-item" key={key}>
                <div className="item-header">
                    <span className="num-lock-status">
                        <span className="goal_id">#{goalEntry.goal.id}</span>
                        <span className={`lock-status-ico fa fa-${accessIconClass}`} />
                        {goalEntry.status === statuses.paused
                            ? (
                                <CustomTooltip id={`icon-paused-${key}`} tooltip={staticNames.paused}>
                                    <span className={`paused-status-ico fa fa-pause-circle`} />
                                </CustomTooltip>
                            )
                            : null
                        }
                        {renderConversionCap(goalEntry)}
                    </span>
                    <span className="goals-header">
                        {goalEntry.status === statuses.paused ? '[ PAUSED ] ' : ''}
                        {goalEntry.goal.title}
                    </span>
                    {state.offer.default_link
                        ? (
                            <a
                                className="link-icon"
                                href="#!"
                                onClick={() => {showConstructor(goalEntry.id, goalEntry.access_granted)}}
                            >
                                <span className="fa fa-link" />
                            </a>
                        )
                        : null
                    }
                </div>
                <div className="item-description">
                    <p>{goalEntry.goal.description}</p>
                </div>
                {renderGoalActions(goalEntry, key)}
            </div>
        );
    }, [getAvailableRegions, renderConversionCap, renderGoalActions, showConstructor, state.offer.default_link, t]);

    const staticNames = t?.offersPage?.offer || {};
    let goalEntry = state.goalEntries[0];

    if (!goalEntry) {
        return <Loading center={true} />;
    }

    return (
        <div className="offer-item__goals-list">
            <div className="offer-item__offer-info">
                <div className="offers__item-descr">
                    <p className="offer-name bold">
                        {goalEntry.status === statuses.paused ? '[ PAUSED ] ' : ''}
                        {goalEntry.offer.title}
                        {renderBadge(goalEntry)}
                    </p>
                    <div className="offers__item-description"> <span> {goalEntry.goal.description} </span></div>
                    <div className="offers__item-goal"> <span>{staticNames.goal}:</span>
                        <span className="goal-name">{goalEntry.goal.title}</span>
                    </div>
                    <p className="offers__item-locales">
                        {staticNames.geo}: {goalEntry.goal.offer.locales.map( (locale,i) =>
                        <span className={`fi fi-${locale}`} key={i}></span>
                    )}
                    </p>
                </div>
            </div>
            <div className="offer-item__offer-description"></div>
            {state.loading
                ? <Loading center={true} />
                : state.goalEntries.map(renderGoalEntries)
            }
        </div>
    );
};

OfferPageGoals.propTypes = {
    offer: PropTypes.object,
    goalEntries: PropTypes.array,
    goalActions: PropTypes.array,
    regions: PropTypes.object,
    loading: PropTypes.bool,
    t: PropTypes.object
};

OfferPageGoals.defaultProps = {
    offer: {},
    goalEntries: [],
    goalActions: [],
    regions: {},
    loading: false,
    t: {}
};

export default withTranslation(OfferPageGoals);